import { ErrorCodes, IPersonalInfoCmsMobile } from '@vfit/consumer/data-access';
import { computeInverse, ErrorDetail, IFiscalCodeReverse } from '@vfit/shared/data-access';
import { IGenericError } from '@vfit/shared/models';
import { purify } from '@vfit/shared/themes';

export const isFiscalCodeValid = (fiscalCode: string): boolean =>
  !!(fiscalCode && fiscalCode.length === 16);

export const checkFiscalCode = (
  fiscalCode: string,
  ageLimit = 18,
  cmsErrors?: IGenericError
): {
  errorCode: ErrorCodes | string;
  errorMessage?: ErrorDetail;
  isError: boolean;
  fiscalCodeIsValid: boolean;
} => {
  const cfData: IFiscalCodeReverse | null = computeInverse(fiscalCode) as IFiscalCodeReverse;

  const getAge = (birthDateString) => {
    const today = new Date();
    const birthDate = new Date(birthDateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      // eslint-disable-next-line no-plusplus
      age--;
    }
    if (age === ageLimit) {
      if (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) {
        return age - 1;
      }
      if (today.getMonth() < birthDate.getMonth()) {
        return age - 1;
      }
      return age;
    }
    return age;
  };
  const day = cfData?.day;
  const year = +cfData?.year;
  const month = cfData?.month;
  const age = getAge(`${month}/${day}/${year}`);

  if (!cfData) {
    return {
      errorCode: ErrorCodes.FISCAL_CODE_ERROR,
      errorMessage: checkError(ErrorCodes.FISCAL_CODE_ERROR, undefined, cmsErrors),
      isError: false,
      fiscalCodeIsValid: false,
    };
  } else if (!cfData.year || age < ageLimit) {
    return {
      errorCode: ErrorCodes.FISCAL_CODE_UNDERAGE,
      errorMessage: checkError(ErrorCodes.FISCAL_CODE_UNDERAGE, undefined, cmsErrors),
      isError: true,
      fiscalCodeIsValid: false,
    };
  } else {
    return {
      errorCode: '',
      isError: false,
      fiscalCodeIsValid: true,
    };
  }
};

export const getCmsValidationConfig = (
  dataFromCms?: IPersonalInfoCmsMobile
): {
  requiredField?: string;
  validFiscalCode?: string;
  validName?: string;
  validSurname?: string;
  surnameMinLen?: string;
  validDate?: string;
  nameMinLen?: string;
} => {
  const validations = dataFromCms?.editorialconfigs?.elements || [];
  const getValidationByID = (id: string) => {
    const parser = new DOMParser();
    const value = validations?.find((e) => e.id === id)?.value || '';
    const doc = parser.parseFromString(purify([value || ''])?.[0] || '', 'text/html');
    return doc.body.textContent || '';
  };
  return {
    requiredField: getValidationByID('requiredField'),
    validFiscalCode: getValidationByID('validFiscalCode'),
    validName: getValidationByID('validName'),
    validSurname: getValidationByID('validSurname'),
    surnameMinLen: getValidationByID('surnameMinLen'),
    validDate: getValidationByID('validDate'),
    nameMinLen: getValidationByID('nameMinLen'),
  };
};

export const organizeRetriveFiscalCodeMobile = (nameFiscalCodeCms: IPersonalInfoCmsMobile) => ({
  retriveFiscalCode: {
    title: nameFiscalCodeCms?.personalinfomobile?.fiscalCode?.title || '',
    description: nameFiscalCodeCms?.personalinfomobile?.fiscalCode?.description || '',
    firstName: nameFiscalCodeCms?.personalinfomobile?.selectInput?.firstName || '',
    lastName: nameFiscalCodeCms?.personalinfomobile?.selectInput?.lastName || '',
    birthDate: nameFiscalCodeCms?.personalinfomobile?.selectInput?.birthdate || '',
    gender: nameFiscalCodeCms?.personalinfomobile?.selectInput?.gender || '',
    nationality: nameFiscalCodeCms?.personalinfomobile?.selectInput?.country || '',
    birthPlace: nameFiscalCodeCms?.personalinfomobile?.selectInput?.city || '',
    birthNation:
      nameFiscalCodeCms?.personalinfomobile?.selectInput?.birthNation || 'Nazione di nascita',
    errorMessage: nameFiscalCodeCms?.personalinfomobile?.firstCard?.allFieldsAreRequired || '',
    buttonLabel: nameFiscalCodeCms?.personalinfomobile?.fiscalCode?.buttonLabel || '',
  },
  nameFiscalCodeCard: {
    title: nameFiscalCodeCms?.personalinfomobile?.firstCard?.title || '',
    titleReadOnly:
      nameFiscalCodeCms?.personalinfomobile?.firstCard?.titleReadOnly ||
      nameFiscalCodeCms?.personalinfomobile?.firstCard?.title ||
      '',
    description: nameFiscalCodeCms?.personalinfomobile?.firstCard?.description || '',
    descriptionReadOnly:
      nameFiscalCodeCms?.personalinfomobile?.firstCard?.descriptionReadOnly ||
      nameFiscalCodeCms?.personalinfomobile?.firstCard?.description ||
      '',
    firstNameLable: nameFiscalCodeCms?.personalinfomobile?.selectInput?.firstName || '',
    lastNameLable: nameFiscalCodeCms?.personalinfomobile?.selectInput?.lastName || '',
    fiscalCodeLable: nameFiscalCodeCms?.personalinfomobile?.selectInput?.fiscalCode || '',
    buttonLabel: nameFiscalCodeCms?.personalinfomobile?.firstCard?.fcMissingLabel || '',
  },
});

export const checkError = (
  errorCode: string,
  handleOnClose: (() => void) | undefined,
  errorMessge?: IGenericError
): ErrorDetail => {
  const title = errorMessge?.title || '';
  let message = errorMessge?.description || '';
  const actionText = errorMessge?.buttonLabel || '';
  let onClose: (() => void) | undefined = handleOnClose;
  let actionEvent: (() => void) | undefined = handleOnClose;
  if (errorCode === ErrorCodes.FISCAL_CODE_UNDERAGE) {
    message = 'Devi essere maggiorenne per proseguire';
    onClose = undefined;
    actionEvent = undefined;
  }
  if (errorCode === ErrorCodes.FISCAL_CODE_ERROR) {
    message = 'Il codice fiscale non è valido. Inserisci un codice fiscale valido per proseguire';
    onClose = undefined;
    actionEvent = undefined;
  }
  return {
    title,
    message,
    actionText,
    onClose,
    actionEvent,
  };
};
